<template>
  <div v-if="file" class="h-full flex flex-col">
    <v-toolbar density="compact" color="surface" class="border-b shrink-0">
      <v-icon :icon="fileIcon.icon" :color="fileIcon.color" class="ms-3" />
      <div v-tooltip="file.name" class="grow min-w-0 truncate text-sm ms-2">{{ file.name }}</div>
      <template v-if="files.length > 1">
        <v-btn
          v-tooltip="t('files.previous')"
          icon="mdi-chevron-left"
          variant="text"
          size="small"
          :disabled="fileIndex <= 0"
          @click="fileId = files[fileIndex - 1]?.id ?? fileId"
        />
        <span class="text-xs text-medium-emphasis">{{
          t("files.counter", { index: fileIndex + 1, total: files.length })
        }}</span>
        <v-btn
          v-tooltip="t('files.next')"
          icon="mdi-chevron-right"
          variant="text"
          size="small"
          :disabled="fileIndex >= files.length - 1"
          @click="fileId = files[fileIndex + 1]?.id ?? fileId"
        />
      </template>
      <v-btn
        v-tooltip="t('common.download')"
        icon="mdi-download"
        variant="text"
        size="small"
        :loading="isDownloading"
        :disabled="isDownloading"
        @click="download(file)"
      />
      <v-btn
        v-tooltip="t('files.close')"
        icon="mdi-close"
        variant="text"
        size="small"
        @click="fileId = null"
      />
    </v-toolbar>
    <div class="grow min-h-0 relative bg-black/5">
      <div v-if="!inlineUrl" class="absolute inset-0 flex items-center justify-center">
        <v-progress-circular v-if="isLoadingUrl" indeterminate color="primary" />
      </div>
      <iframe
        v-else-if="previewKind === 'pdf'"
        :key="inlineUrl"
        :src="inlineUrl"
        :title="file.name"
        class="w-full h-full border-0"
      ></iframe>
      <img
        v-else-if="previewKind === 'image'"
        :src="inlineUrl"
        :alt="file.name"
        class="w-full h-full object-contain p-4"
      />
      <div
        v-else
        class="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center p-6"
      >
        <v-icon :icon="fileIcon.icon" :color="fileIcon.color" size="64" />
        <div class="text-sm text-medium-emphasis">{{ t("files.previewUnavailable") }}</div>
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-download"
          :loading="isDownloading"
          :disabled="isDownloading"
          @click="download(file)"
        >
          {{ t("common.download") }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup generic="FileItem extends TransactionFile">
import { useI18n } from "@/composables/useI18n";
import { useToast } from "@/store";
import { downloadUrl } from "@/utils/downloadFile";
import { getFileIcon, type FileUrlDisposition } from "@/utils/files";
import type { TransactionFile } from "@aircap/common/transaction";
import { captureException } from "@sentry/vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps<{
  files: FileItem[];
  getUrl: (file: FileItem, disposition: FileUrlDisposition) => Promise<string>;
}>();

const fileId = defineModel<string | null>("fileId", { required: true });

const { t } = useI18n();
const { showErrorToast } = useToast();

const fileIndex = computed(() => props.files.findIndex((file) => file.id === fileId.value));
const file = computed(() => props.files[fileIndex.value]);
const fileIcon = computed(() => getFileIcon(file.value ?? { name: "", type: "" }));
const previewKind = computed(() => {
  if (file.value?.type === "application/pdf") return "pdf";
  if (file.value?.type.startsWith("image/")) return "image";
  return "unsupported";
});

const inlineUrl = ref<string | null>(null);
const isLoadingUrl = ref(false);

watch(
  () => file.value?.id,
  async () => {
    inlineUrl.value = null;
    const currentFile = file.value;
    if (!currentFile) return;
    isLoadingUrl.value = true;
    try {
      const url = await props.getUrl(currentFile, "inline");
      if (file.value?.id === currentFile.id) inlineUrl.value = url;
    } catch (cause) {
      const error = new Error(`Error getting preview url for file ${currentFile.id}`, { cause });
      console.error(error);
      captureException(error);
      showErrorToast();
    } finally {
      if (file.value?.id === currentFile.id) isLoadingUrl.value = false;
    }
  },
  { immediate: true },
);

const isDownloading = ref(false);

const download = async (currentFile: FileItem) => {
  isDownloading.value = true;
  try {
    const url = await props.getUrl(currentFile, "attachment");
    downloadUrl(url);
  } catch (cause) {
    const error = new Error(`Error downloading file ${currentFile.id}`, { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    isDownloading.value = false;
  }
};

const closeOnEscape = (event: KeyboardEvent) => {
  if (event.key !== "Escape") return;
  // Captured before Vuetify overlays handle Escape (they don't preventDefault), so an open dialog or menu keeps it.
  if (document.querySelector(".v-overlay--active:not(.v-tooltip)")) return;
  fileId.value = null;
};
onMounted(() => window.addEventListener("keydown", closeOnEscape, { capture: true }));
onBeforeUnmount(() => window.removeEventListener("keydown", closeOnEscape, { capture: true }));
</script>
